<template>
<div class="einfach-sortieren">

    <h2>Ablauf</h2>

    <p v-if="ablage.length === 0" class="leer">
        Tippe unten den Spruch an, der zuerst kommt. Die Sprüche wechseln sich links und rechts ab.
    </p>

    <!-- Die Seite haengt am Platz, nicht am Spruch: 1. links, 2. rechts …
         Wer gerufen hat, steht links. So verraet die Seite nichts. Erst
         wenn der Ablauf sitzt, steht die Station dabei. -->
    <TransitionGroup v-else tag="ol" name="baustein" class="ablauf" :class="rueckmeldung && (rueckmeldung.sitzt ? 'stimmt' : 'daneben')">
        <li v-for="(zug, i) in ablage" :key="zug.nr" :class="i % 2 === 0 ? 'seite-links' : 'seite-rechts'">
            <span v-if="geloest" class="station">{{ zug.station.rufname }}</span>
            {{ spruchVon(zug) }}
        </li>
    </TransitionGroup>

    <template v-if="vorrat.length > 0">
        <h2>Sprüche</h2>
        <TransitionGroup tag="div" name="baustein" class="vorrat">
            <button v-for="zug in vorrat" :key="zug.nr" @click="waehlen(zug)">
                {{ spruchVon(zug) }}
            </button>
        </TransitionGroup>
    </template>

    <div v-if="rueckmeldung" class="rueckmeldung" :class="rueckmeldung.sitzt ? 'stimmt' : 'daneben'">
        <p class="satz">{{ rueckmeldung.sitzt ? 'Das sitzt.' : 'Das stimmt noch nicht.' }}</p>
    </div>

    <div class="knopfleiste">
        <button class="haupt" @click="pruefen()" :disabled="vorrat.length > 0 || geloest">Prüfen</button>
        <button @click="rueckgaengig()" :disabled="ablage.length === 0 || geloest">Rückgängig</button>
        <button @click="reset()" :disabled="geloest">Zurücksetzen</button>
        <slot name="rechts" />
    </div>

</div>
</template>

<script>
import { ref } from 'vue';
import { mische } from '@/logic/Uebung';

// Uebungsart "Einfach sortieren": die Sprueche kommen fertig, nur die
// Reihenfolge des Ablaufs ist zu legen — wer spricht wann.
// Jeder Tipp haengt einen Spruch hinten an. Zurueck geht nur der letzte:
// wuerde einer aus der Mitte entfernt, spraenge alles dahinter auf die
// andere Seite. Darum auch kein hoch/runter.
// Geht nur mit Ablaeufen, in denen sich die Stationen abwechseln — siehe
// wechseltAb() in Uebung.js und passt() in Uebungsarten.js.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe
// AllesSortieren.vue). Ein Pruefen prueft die ganze Uebung, darum ist
// sitzt === geloest.
export default {
    inheritAttrs: false,
    props: {
        uebung: {type: Object, required: true}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const vorrat = ref(mische(props.uebung.zuege))
        const ablage = ref([])
        // ref, kein computed — wie bei "Alles sortieren": Rueckmeldung nur
        // auf "Pruefen" hin, und jede Aenderung laesst sie vergehen.
        const rueckmeldung = ref(null)
        const geloest = ref(false)

        const spruchVon = (zug) => zug.teile.map(t => t.text).join(' ')

        const waehlen = (zug) => {
            vorrat.value = vorrat.value.filter(z => z !== zug)
            ablage.value.push(zug)
            rueckmeldung.value = null
        }

        const rueckgaengig = () => {
            vorrat.value.push(ablage.value.pop())
            rueckmeldung.value = null
        }

        const reset = () => {
            vorrat.value = mische(props.uebung.zuege)
            ablage.value = []
            rueckmeldung.value = null
        }

        // Verglichen wird der Text, nicht der Zug: zwei gleich lautende
        // Sprueche waeren austauschbar.
        const pruefen = () => {
            const sitzt = ablage.value.every((zug, i) => spruchVon(zug) === spruchVon(props.uebung.zuege[i]))
            if (sitzt) geloest.value = true
            rueckmeldung.value = { sitzt }
            emit('geprueft', { sitzt, geloest: sitzt })
        }

        return { vorrat, ablage, rueckmeldung, geloest, spruchVon, waehlen, rueckgaengig, reset, pruefen }
    }
}
</script>

<style scoped>
/* Abschnittstitel schmal in Versalien, eine Haarlinie bis zum Rand. */
h2 {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0 0 0.6rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

h2::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--zs-rahmen);
}

.hinweis {
    margin: 0 0 1.75rem;
    font-size: 0.95rem;
    color: var(--zs-grau);
}

/* ─── Ablauf ─────────────────────────────────────────────────
   Der Rahmen ist immer da und faerbt sich nur beim Pruefen. */

.ablauf {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0 0 1.75rem;
    padding: 0.6rem;
    list-style: none;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.ablauf.stimmt {
    background: var(--zs-oliv-hell);
    border-color: var(--zs-oliv);
    box-shadow: 0 0 0 1px var(--zs-oliv);
}

.ablauf.daneben {
    border-color: var(--zs-warnrot);
    box-shadow: 0 0 0 1px var(--zs-warnrot);
}

.ablauf li {
    max-width: 80%;
    padding: 0.6rem 0.85rem;
    line-height: 1.4;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

/* Links ruft, rechts antwortet: die Seite haengt am Platz. */
.ablauf .seite-links {
    align-self: flex-start;
    border-left: 3px solid var(--zs-blau);
}

.ablauf .seite-rechts {
    align-self: flex-end;
    border-right: 3px solid var(--zs-grau);
}

.station {
    display: block;
    margin-bottom: 0.2rem;
    font-family: 'Archivo', system-ui, sans-serif;
    font-size: 0.8rem;
    font-weight: 650;
    color: var(--zs-blau);
}

/* ─── Vorrat ─────────────────────────────────────────────────
   Ganze Sprueche sind lang: einer pro Zeile statt nebeneinander. */

.vorrat {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.75rem;
}

.vorrat button {
    min-height: 2.5rem;
    padding: 0.5rem 0.85rem;
    font: inherit;
    line-height: 1.4;
    color: var(--zs-tinte);
    /* Quelle hellgrau, Ziel (Ablage) weiss: man sieht, was wohin geht. */
    background: var(--zs-flaeche);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
    text-align: left;
}

.vorrat button:hover {
    background: var(--zs-tafel);
    border-color: var(--zs-tinte);
}

/* ─── Rueckmeldung ───────────────────────────────────────────
   Nie nur Farbe: vor dem Satz steht ein Haken oder ein Kreuz. */

.rueckmeldung {
    margin: 0 0 1.75rem;
    padding: 0.85rem 1rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.rueckmeldung.stimmt {
    background: var(--zs-oliv-hell);
    border-color: rgba(85, 107, 47, 0.45);
}

.rueckmeldung.daneben {
    background: var(--zs-warnrot-hell);
    border-color: rgba(179, 38, 30, 0.4);
}

.satz {
    margin: 0;
    font-weight: 650;
}

.stimmt .satz {
    color: var(--zs-oliv);
}

.daneben .satz {
    color: var(--zs-warnrot);
}

.stimmt .satz::before {
    content: '\2713\00a0\00a0';
}

.daneben .satz::before {
    content: '\2715\00a0\00a0';
}

.rueckmeldung ul {
    margin: 0.5rem 0 0;
    padding-left: 1.6rem;
}

.rueckmeldung li {
    margin-bottom: 0.2rem;
    line-height: 1.45;
    color: var(--zs-tinte);
}
/* ─── Knopfleiste ────────────────────────────────────────────
   Rechts kommt, was dem Container gehoert (Neue Uebung / Weiter). */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--zs-rahmen);
}

.knopfleiste button {
    min-height: 2.5rem;
    padding: 0.45rem 1rem;
    font: inherit;
    font-weight: 600;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.knopfleiste button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

/* Die eigentliche Handlung der Uebung (Pruefen / Senden). */
.knopfleiste .haupt {
    color: #ffffff;
    background: var(--zs-blau);
    border-color: var(--zs-blau);
}

.knopfleiste .haupt:hover:not(:disabled) {
    background: var(--zs-blau-dunkel);
    border-color: var(--zs-blau-dunkel);
}

.knopfleiste button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.ablauf li,
.vorrat button {
    font-family: var(--zs-funkschrift);
}

@media (prefers-reduced-motion: reduce) {
    .vorrat button {
        transition: none;
    }
}
/* ─── Bewegung zeigt, was sich geaendert hat ─────────────────
   Neue Bausteine gleiten kurz herein, umgestellte schieben an ihren
   Platz. Entfernte verschwinden sofort (kein Warten aufs Ausblenden). */
.baustein-enter-active {
    transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}

.baustein-enter-from {
    opacity: 0;
    transform: translateY(6px);
}

.baustein-move {
    transition: transform 0.2s ease-out;
}

/* Leere Ablage: gestrichelter Zielbereich, damit man sieht, wohin die
   Bausteine kommen. */
.leer {
    display: flex;
    align-items: center;
    min-height: 3.25rem;
    margin: 0 0 1.75rem;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    color: var(--zs-grau);
    background: rgba(255, 255, 255, 0.55);
    border: 1px dashed #9AA3B2;
    border-radius: var(--zs-ecke);
    box-sizing: border-box;
}

@media (prefers-reduced-motion: reduce) {
    .baustein-enter-active,
    .baustein-move {
        transition: none;
    }
}
</style>
